import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRestaurantContext } from '@/context/RestaurantContext';
import { useAuth } from '@/context/auth';
import { useSmsSubscribers } from '@/hooks/sms/useSmsSubscribers';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';
import SubscribersList from '@/components/sms/subscribers/SubscribersList';
import SubscriberDetails from '@/components/sms/subscribers/SubscriberDetails';
import SubscriptionAnalytics from '@/components/sms/subscribers/SubscriptionAnalytics';
import { SmsCustomer } from '@/hooks/sms/types';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserRole } from '@/context/auth/types';

const SubscribersTab: React.FC = () => {
  const { user } = useAuth();
  const { selectedRestaurants, setSelectedRestaurants, activeRestaurantDetails } = useRestaurantContext();
  // Always use forceRefresh to ensure cache invalidation and support targeted refresh
  const { subscribers, loading, error, refreshSubscribers, refreshTargetedSubscriber } = useSmsSubscribers(user, selectedRestaurants);
  
  // Simple wrapper function that handles string IDs
  const forceRefreshSubscribers = (subscriberId?: string | any) => {
    // If no ID provided, just do a regular refresh
    if (!subscriberId) {
      refreshSubscribers(true);
      return;
    }
    
    // Handle string IDs and extract ID from objects if needed
    const idToRefresh = typeof subscriberId === 'string' 
      ? subscriberId 
      : (subscriberId?.id || null);
    
    // Only refresh with valid string IDs
    if (idToRefresh && typeof idToRefresh === 'string' && refreshTargetedSubscriber) {
      refreshTargetedSubscriber(idToRefresh);
    } else {
      refreshSubscribers(true);
    }
  };
  
  const [activeTab, setActiveTab] = useState<'list' | 'details' | 'analytics'>('list');
  const [selectedSubscriber, setSelectedSubscriber] = useState<SmsCustomer | null>(null);
  
  const handleSubscriberSelect = (subscriber: SmsCustomer) => {
    setSelectedSubscriber(subscriber);
    setActiveTab('details');
  };
  
  const handleBackToList = () => {
    setActiveTab('list');
    setSelectedSubscriber(null);
  };

  const handleRestaurantChange = (value: string) => {
    if (value === 'all') {
      setSelectedRestaurants(['all']);
    } else {
      setSelectedRestaurants([value]);
    }
  };

  const handleExportSubscribers = async () => {
    try {
      // Implementation for exporting subscribers to CSV
      console.log('Exporting subscribers...');
      // Future API call: await exportSubscribersToCSV(selectedRestaurants);
      
      // Simple CSV generation for now
      if (subscribers && subscribers.length > 0) {
        const headers = ['Name', 'Email', 'Phone', 'Status', 'Total Orders', 'Total Spend', 'Last Order'];
        const csvContent = [
          headers.join(','),
          ...subscribers.map(sub => {
            return [
              `"${sub.name || 'Unknown'}"`,
              `"${sub.email || ''}"`,
              `"${sub.phone || ''}"`,
              `"${sub.status || 'unknown'}"`,
              sub.totalOrders || 0,
              `"$${typeof sub.totalSpend === 'number' ? sub.totalSpend.toFixed(2) : sub.totalSpend}"`,
              `"${sub.lastOrderFormatted || sub.lastOrder || 'Never'}"`
            ].join(',');
          })
        ].join('\n');
        
        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `sms_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Failed to export subscribers:', err);
    }
  };

  // Explicitly type the user role with UserRole enum
  const canAccessAnalytics = user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ORG_ADMIN;
  const canExport =
    user?.role === UserRole.SUPER_ADMIN ||
    user?.role === UserRole.ORG_ADMIN ||
    user?.role === UserRole.REST_ADMIN;

  const showNoRestaurantWarning = selectedRestaurants.length === 0;

  // Refresh subscribers when restaurant selection changes
  useEffect(() => {
    refreshSubscribers(true);
  }, [selectedRestaurants]);

  // Handle restaurant change while viewing subscriber details
  useEffect(() => {
    if (activeTab === 'details' && selectedSubscriber && subscribers) {
      // Find the updated subscriber data for the new restaurant
      const updatedSubscriber = subscribers.find(sub => sub.id === selectedSubscriber.id);

      if (updatedSubscriber) {
        // Update the selected subscriber with the new data (including restaurant-specific status)
        setSelectedSubscriber(updatedSubscriber);
      } else {
        // If subscriber doesn't exist in the new restaurant's list, go back to list
        handleBackToList();
      }
    }
  }, [selectedRestaurants, activeTab, subscribers]);

  if (loading) {
    return (
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium">SMS Subscribers</h3>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-center items-center h-40">
              <div className="animate-pulse flex flex-col items-center">
                <div className="h-16 w-16 bg-gray-200 rounded-full mb-4"></div>
                <div className="h-4 w-40 bg-gray-200 rounded mb-3"></div>
                <div className="h-3 w-32 bg-gray-200 rounded"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium">SMS Subscribers</h3>
          <Button onClick={forceRefreshSubscribers} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" /> Retry
          </Button>
        </div>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error Loading Subscribers</AlertTitle>
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h3 className="text-lg font-medium">
          {activeTab === 'list' ? 'SMS Subscribers' : activeTab === 'details' ? 'Subscriber Details' : 'Subscription Analytics'}
        </h3>
        <div className="flex flex-wrap gap-2">
          {activeTab !== 'list' && (
            <Button variant="outline" size="sm" onClick={handleBackToList}>
              Back to List
            </Button>
          )}
          {activeTab === 'list' && canExport && (
            <Button variant="outline" size="sm" onClick={handleExportSubscribers} className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Restaurants:</span>
            <Select value={selectedRestaurants[0]} onValueChange={handleRestaurantChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Restaurants</SelectItem>
                {user?.restaurants?.map((restaurant) => (
                  <SelectItem key={restaurant.id} value={restaurant.id}>
                    {restaurant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={forceRefreshSubscribers} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>
      
      {showNoRestaurantWarning ? (
        <Alert variant="default" className="mb-4 border-amber-500 bg-amber-50 text-amber-900">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription>
            Please select a restaurant to view and manage subscribers
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <Tabs value={activeTab} onValueChange={(val: string) => setActiveTab(val as 'list' | 'details' | 'analytics')}>
            <TabsList className="mb-4">
              <TabsTrigger value="list">Subscribers List</TabsTrigger>
              {selectedSubscriber && <TabsTrigger value="details">Subscriber Details</TabsTrigger>}
              {canAccessAnalytics && <TabsTrigger value="analytics">Analytics</TabsTrigger>}
            </TabsList>
            
            {activeTab === 'list' && (
              <SubscribersList 
                subscribers={subscribers || []} 
                onSubscriberSelect={handleSubscriberSelect}
                onRefresh={forceRefreshSubscribers}
                userRole={user?.role as UserRole}
              />
            )}
            
            {activeTab === 'details' && selectedSubscriber && (
              <SubscriberDetails 
                subscriber={selectedSubscriber} 
                onBack={handleBackToList}
                onUpdate={(subscriberId) => forceRefreshSubscribers(subscriberId)}
                userRole={user?.role as UserRole}
              />
            )}
            
            {activeTab === 'analytics' && canAccessAnalytics && (
              <SubscriptionAnalytics 
                subscribers={subscribers || []}
                selectedRestaurants={selectedRestaurants}
              />
            )}
          </Tabs>
          
        </>
      )}
    </div>
  );
};

export default SubscribersTab;
